import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone } from 'lucide-react';
import logo from '../assets/logo.png';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(() => window.__lgInstallPrompt || null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }

    // If early prompt was already captured
    if (window.__lgInstallPrompt && !deferredPrompt) {
      setDeferredPrompt(window.__lgInstallPrompt);
      const dismissed = localStorage.getItem('lg_install_dismissed');
      if (!dismissed) {
        setTimeout(() => setShow(true), 3500);
      }
    }

    // Listen for install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      window.__lgInstallPrompt = e;
      const dismissed = localStorage.getItem('lg_install_dismissed');
      if (!dismissed) {
        setTimeout(() => setShow(true), 3500);
      }
    };

    const handleCustomPrompt = (e) => {
      if (e.detail) {
        setDeferredPrompt(e.detail);
      }
    };

    const handleOpenPrompt = () => {
      const promptToUse = window.__lgInstallPrompt || deferredPrompt;
      if (promptToUse) {
        promptToUse.prompt();
        promptToUse.userChoice.then(({ outcome }) => {
          if (outcome === 'accepted') {
            setShow(false);
            setDeferredPrompt(null);
            window.__lgInstallPrompt = null;
          }
        }).catch(() => {});
      } else {
        setShow(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('lg:installprompt', handleCustomPrompt);
    window.addEventListener('lg:open-install-prompt', handleOpenPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('lg:installprompt', handleCustomPrompt);
      window.removeEventListener('lg:open-install-prompt', handleOpenPrompt);
    };
  }, [deferredPrompt]);

  const handleInstall = async () => {
    const promptToUse = deferredPrompt || window.__lgInstallPrompt;
    if (!promptToUse) return;
    promptToUse.prompt();
    const { outcome } = await promptToUse.userChoice;
    if (outcome === 'accepted') {
      setShow(false);
      setDeferredPrompt(null);
      window.__lgInstallPrompt = null;
    }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('lg_install_dismissed', 'true');
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.96 }}
          transition={{ type: 'spring', damping: 26, stiffness: 260 }}
          className="fixed bottom-20 lg:bottom-6 left-4 right-4 z-50 max-w-sm mx-auto"
        >
          <div className="bg-white dark:bg-[#161514] rounded-2xl p-4 shadow-xl border border-[#e2e0db] dark:border-[#2e2d2b] relative">
            <button
              onClick={handleDismiss}
              className="absolute top-3.5 right-3.5 w-6 h-6 bg-[#f8f7f4] dark:bg-[#201f1d] hover:bg-[#edeae4] dark:hover:bg-[#2a2826] rounded-full flex items-center justify-center text-[#6b6860] dark:text-[#a8a49c] transition-colors"
              title="Dismiss"
            >
              <X size={13} />
            </button>

            <div className="flex items-center gap-3 mb-3.5 pr-6">
              <div className="relative shrink-0">
                <img
                  src={logo}
                  alt="Lucy Garden"
                  className="w-11 h-11 rounded-xl object-cover ring-1 ring-black/10 dark:ring-white/10"
                />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-md bg-[#1e3a5f] text-white flex items-center justify-center text-[9px] shadow-xs">
                  <Smartphone size={10} />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <h3 className="font-bold text-xs text-[#1a1917] dark:text-[#f5f4f0] truncate">
                    Install Lucy Garden
                  </h3>
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-blue-50 dark:bg-blue-950/50 text-[#1e3a5f] dark:text-[#93c5fd] px-1 py-0.2 rounded border border-blue-200 dark:border-blue-900/40">
                    App
                  </span>
                </div>
                <p className="text-[11px] text-[#6b6860] dark:text-[#a8a49c] mt-0.5 leading-tight">
                  Add to home screen for faster wholesale ordering.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleDismiss}
                className="flex-1 py-2 rounded-xl font-bold text-xs text-[#6b6860] dark:text-[#a8a49c] bg-[#f8f7f4] dark:bg-[#201f1d] hover:bg-[#edeae4] dark:hover:bg-[#2a2826] border border-[#e2e0db] dark:border-[#2e2d2b] transition-colors"
              >
                Not Now
              </button>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleInstall}
                className="flex-1 py-2 rounded-xl font-bold text-xs text-white bg-[#1e3a5f] hover:bg-[#162d4a] shadow-xs flex items-center justify-center gap-1.5 transition-colors"
              >
                <Download size={13} />
                <span>Install App</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
